import React, { FC, useState, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import classnames from 'classnames'
import { Dropdown, Menu } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import UnityModal, { CustomModalProps } from 'src/pc/components/CloudGame/UnityModal'

export const formatLastTime = (timestamp: number) => {
  if (typeof timestamp !== 'number') {
    return ['00', '00', '00']
  }
  const second = (timestamp / 1000) | 0
  const h = (second / 60 / 60) | 0
  const m = ((second % (60 * 60)) / 60) | 0
  const s = second % 60 | 0
  return [h, m, s].map(a => a.toString().padStart(2, '0'))
}

const MenuItem = Menu.Item

const ConfigOptions1 = [
  { label: '自适用 (推荐)', value: 0 },
  { label: '手动设置', value: 1 },
]

const ConfigOptions2 = [
  { label: '开启', value: 0 },
  { label: '关闭', value: 1 },
]

type TBaseType = number | string

interface TScreenConfig {
  width: string
  height: string
}

export interface Config {
  showDelay: boolean
  autoSetScreen: boolean
  screenConfig: TScreenConfig
  fpsConfig: string
}

export const InitConfig: Config = {
  showDelay: true,
  autoSetScreen: true,
  screenConfig: { width: '1920', height: '1080' },
  fpsConfig: '60',
}

export const WindowSettingsModal: FC<
  CustomModalProps & { defaultConfig: Config; configChange: (config: Config) => void }
> = ({ visible, defaultConfig, configChange, ...props }) => {
  const [config, setConfig] = useState<Config>(defaultConfig)

  useEffect(() => {
    setConfig(defaultConfig)
  }, [defaultConfig, visible])

  const onMenuClick = (key: string, type: 'screen' | 'fps') => {
    if (type === 'screen') {
      const [width, height] = key.split('*')
      setConfig(c => ({ ...c, screenConfig: { width, height } }))
    } else if (type === 'fps') {
      setConfig(c => ({ ...c, fpsConfig: key }))
    }
  }

  /** 保存配置 */
  const applyConfig = (e: React.MouseEvent<HTMLElement>) => {
    configChange(config)
    props?.onOk?.(e)
  }

  const screenConfigChange = (value: TBaseType) => {
    const autoSetScreen = value === 0
    let screenConfig = config.screenConfig
    let fpsConfig = config.fpsConfig
    if (autoSetScreen) {
      screenConfig = InitConfig.screenConfig
      fpsConfig = InitConfig.fpsConfig
    }
    setConfig(c => ({ ...c, screenConfig, fpsConfig, autoSetScreen }))
  }

  const fpsConfigChange = (value: TBaseType) => {
    setConfig(config => ({ ...config, showDelay: value === 0 }))
  }

  const currentConfig = useMemo(() => {
    const { width, height } = config.screenConfig
    return {
      screenConfig: [width, height].join('*'),
      fpsConfig: config.fpsConfig,
    }
  }, [config])

  const Menu1 = (
    <StyledMenu onClick={(e: any) => onMenuClick(e.key + '', 'screen')} className="custom">
      <MenuItem key="1920*1080">
        <span className="clr-878787">1920*1080</span>
      </MenuItem>
      <MenuItem key="1280*720">
        <span className="clr-878787">1280*720</span>
      </MenuItem>
    </StyledMenu>
  )
  const Menu2 = (
    <StyledMenu onClick={(e: any) => onMenuClick(e.key + '', 'fps')} className="custom">
      <MenuItem key="60">
        <span className="clr-878787">60</span>
      </MenuItem>
      <MenuItem key="30">
        <span className="clr-878787">30</span>
      </MenuItem>
    </StyledMenu>
  )

  return (
    <UnityModal visible={visible} {...props} destroyOnClose onOk={applyConfig}>
      <CustomRadioGroup
        options={ConfigOptions1}
        defaultIndex={config.autoSetScreen ? 0 : 1}
        onChange={screenConfigChange}
      />
      <StyledRow className={classnames({ 'not-allowed': config.autoSetScreen })}>
        <label className="label">分辨率</label>
        <Dropdown overlay={Menu1} placement="bottomCenter" trigger={['click']}>
          <StyledSelectButton className={classnames({ disable: config.autoSetScreen })}>
            <span>{currentConfig.screenConfig}</span>
            <DownOutlined />
          </StyledSelectButton>
        </Dropdown>
      </StyledRow>
      <StyledRow className={classnames({ 'not-allowed': config.autoSetScreen })}>
        <label className="label">帧 率</label>
        <Dropdown overlay={Menu2} placement="bottomCenter" trigger={['click']}>
          <StyledSelectButton className={classnames({ disable: config.autoSetScreen })}>
            <span>{currentConfig.fpsConfig}</span>
            <DownOutlined />
          </StyledSelectButton>
        </Dropdown>
      </StyledRow>
      <StyledRowText>延迟展示</StyledRowText>
      <CustomRadioGroup options={ConfigOptions2} defaultIndex={config.showDelay ? 0 : 1} onChange={fpsConfigChange} />
    </UnityModal>
  )
}

export const TimeShow: FC<{ time: number; label?: string }> = ({ time, label }) => {
  const [lastTime, setLastTime] = useState<number>(0)
  const [timer, setTimer] = useState<number | null>(null)

  const formatTimes = useMemo(() => {
    return formatLastTime(lastTime)
  }, [lastTime])

  useEffect(() => {
    clearTimer()
    setLastTime(Math.max(time, 0))
    let ti = time
    const t = window.setInterval(() => {
      ti -= 1000
      if (ti < 0) {
        clearTimer()
      }
      setLastTime(Math.max(ti, 0))
    }, 1000)
    setTimer(t)
  }, [time])

  useEffect(() => {
    clearTimer()
    return () => {
      clearTimer()
    }
  }, [])

  const clearTimer = () => {
    if (timer) {
      clearInterval(timer)
      setTimer(null)
    }
  }

  return (
    <StyledTimeShowBox>
      {label && <span className="box-label">{label}</span>}
      {formatTimes.map((t: string, i: number) => (
        <span key={i}>
          <span className="t-span">{t}</span>
          {i < 2 && <span className="t-delimiter">:</span>}
        </span>
      ))}
    </StyledTimeShowBox>
  )
}

type RadioOption = {
  [p in string | number]: TBaseType
} & {
  label: string
  value: TBaseType
}

export const CustomRadioGroup: FC<{
  defaultIndex?: number
  options: RadioOption[]
  onChange?: (value: string | number, opt: RadioOption, index: number) => void
}> = ({ defaultIndex, options, onChange }) => {
  const [active, setActive] = useState<number>(defaultIndex || 0)

  const onRadioChange = (index: number) => {
    setActive(index)
  }
  useEffect(() => {
    if (options.length) {
      const opt = options[active]
      onChange?.(opt.value, opt, active)
    }
  }, [active, options])
  return (
    <StyledRadioGroup>
      {options.map((option, index) => (
        <StyledRadioItem key={index} onClick={() => onRadioChange(index)}>
          <StyledCheck className={classnames({ active: active === index })} />
          <div className="radio-label">{option.label}</div>
        </StyledRadioItem>
      ))}
    </StyledRadioGroup>
  )
}

const StyledTimeShowBox = styled.div`
  display: flex;
  align-items: center;
  .box-label {
    font-size: 0.14rem;
    color: #ccc;
    margin: 0 0.08rem;
  }
  .t-span {
    display: inline-block;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 0.04rem;
    border: 0.01rem solid var(--main-color);
    color: #fff;
    font-weight: bold;
    font-size: 0.14rem;
    letter-spacing: 0.02rem;
    padding: 0.02rem 0.04rem;
  }
  .t-delimiter {
    margin: 0 0.08rem;
    color: #fff;
    font-weight: bold;
  }
`

const StyledRowText = styled.div`
  font-size: var(--fontsize-16);
  color: var(--text-color-4);
  margin: 0.2rem 0;
`

const StyledRadioGroup = styled.div`
  display: flex;
  align-items: center;
`
const StyledRadioItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  &:not(:last-child) {
    margin-right: 0.3rem;
  }
  .radio-label {
    font-size: 0.14rem;
    color: var(--text-color-4);
  }
`

const StyledCheck = styled.div`
  width: 0.18rem;
  height: 0.18rem;
  background: rgba(216, 216, 216, 0.1);
  box-shadow: 0px 0.02rem 0.12rem 0px rgba(0, 0, 0, 0.6);
  border: 1px solid #696969;
  cursor: pointer;
  border-radius: 50%;
  margin-right: 10px;
  &.active {
    background: #e2e3e7;
    border: 1px solid #e2e3e7;
    &::after {
      content: '';
      display: block;
      width: 8px;
      height: 8px;
      background: #000000;
      box-shadow: 0px 0.02rem 0.12rem 0px rgb(0 0 0 / 60%);
      border-radius: 50%;
      position: relative;
      left: 0.04rem;
      top: 0.04rem;
    }
  }
`

const StyledRow = styled.div<{ labelWidth?: number }>`
  display: flex;
  align-items: center;
  line-height: 0.24rem;
  margin-top: 0.2rem;
  label {
    ${props => `width: ${props.labelWidth || 48}px;`}
    display: inline-block;
    text-align: justify;
    text-align-last: justify;
    margin-right: 0.5em;
    font-size: 0.14rem;
    color: #878787;
  }
`

const StyledSelectButton = styled.button`
  width: 1.3rem;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0.04rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  cursor: pointer;
  display: flex;
  align-items: center;
  span {
    padding: 0 0.06rem;
  }
  &.disable {
    pointer-events: none;
    opacity: 0.6;
    cursor: not-allowed;
  }
  .anticon-down {
    margin-left: auto;
  }
`

const StyledMenu = styled(Menu)`
  .icon {
    font-size: 0.26rem;
  }
  &.ant-dropdown-menu {
    background: #191919;
    box-shadow: 0px 0.02rem 0.08rem 0px rgba(0, 0, 0, 0.5);
    border: 1px solid #252525;
  }
  .ant-dropdown-menu-item,
  .ant-dropdown-menu-submenu-title {
    color: #b0b0b0;
    display: flex;
    align-items: center;
    padding: 0.07rem 0.2rem 0.07rem 0.08rem;
  }
  .ant-dropdown-menu-item > a:hover,
  .ant-dropdown-menu-submenu-title > a:hover,
  .ant-dropdown-menu-item:hover,
  .ant-dropdown-menu-submenu-title:hover {
    color: var(--main-color);
    background: #202020;
  }
`
